import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Menu,
  PackageCheck,
  Clock,
  Archive,
  ClipboardList,
  ArrowRight,
  Send,
  Truck,
} from "lucide-react";

export default function WarehouseDashboard() {
  const { authAxios, user } = useAuth();
  const [stats, setStats] = useState({
    da_trasferire: 0,
    ricevuti: 0,
    fatturati: 0,
    totale: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await authAxios.get("/orders/stats");
        setStats(res.data);
      } catch {
        toast.error("Errore nel caricamento statistiche");
      }
    };
    fetchStats();
  }, [authAxios]);

  const statCards = [
    { label: "Da Trasferire", value: stats.da_trasferire, icon: Send, color: "text-violet-400", bg: "bg-violet-500/10" },
    { label: "Ricevuti", value: stats.ricevuti, icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Fatturati", value: stats.fatturati, icon: Archive, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  ];

  return (
    <div className="min-h-screen bg-[#09090b] flex" data-testid="warehouse-dashboard">
      <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-5xl">
        <PageHeader title="Magazzino" />
        <div className="flex items-center gap-3 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              Ciao, {user?.name || "Magazziniere"}
            </h1>
            <p className="text-sm text-zinc-500">Pannello Magazzino - Triolo.Store</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {statCards.map((s) => (
            <Card key={s.label} className="bg-[#18181b] border-white/5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-lg ${s.bg}`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold font-mono ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <h2 className="text-lg font-semibold text-white mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/warehouse/orders" data-testid="go-orders-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-amber-500/30 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-500/10">
                    <ClipboardList className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Gestisci Ordini</p>
                    <p className="text-xs text-zinc-500">Visualizza e gestisci tutti gli ordini</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/warehouse/orders/new" data-testid="go-new-order-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-violet-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-violet-500/10">
                    <ClipboardList className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Nuovo Ordine</p>
                    <p className="text-xs text-zinc-500">Inserisci un nuovo ordine manuale</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/warehouse/picking" data-testid="go-picking-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-emerald-500/30 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-emerald-500/10">
                    <PackageCheck className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Lista di Scarico</p>
                    <p className="text-xs text-zinc-500">Prodotti raggruppati per picking</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-emerald-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/warehouse/giri" data-testid="go-giri-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-sky-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-sky-500/10">
                    <Truck className="w-6 h-6 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Giri</p>
                    <p className="text-xs text-zinc-500">Giri di presa ordini per cliente</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-sky-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/warehouse/delivery-rounds" data-testid="go-delivery-rounds-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-orange-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-orange-500/10">
                    <PackageCheck className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Giri Consegne</p>
                    <p className="text-xs text-zinc-500">Gestione consegne e file BBB</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-orange-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </main>
    </div>
  );
}
